'use client';

import { CatalogueLink as Link } from '@/components/catalogue-navigation';

import type {
  Category,
  SubCategoriesEntity,
} from '@eliadmin/core/src/api/slugs/types/slug-detail-types';

import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from '../ui/heading';

import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { MdOutlineKeyboardArrowRight } from 'react-icons/md';
import { Breadcrumbs } from '@eliadmin/core/src/components/homepage/breadcrumbs';
import { useBreakpoint } from '@eliadmin/core/src/hooks/use-breakpoint';
import { LuArrowDown } from 'react-icons/lu';

export function CategoryHeader({ category }: { category: Category }) {
  const { t } = useTranslations();

  const isDesktop = useBreakpoint('sm');

  const hasSubCategories = category?.sub_categories && category.sub_categories.length > 0;

  return (
    <section className={cn('custom-section', 'pt-12')}>
      <div
        className={cn(
          'custom-container',
          'flex flex-col items-start justify-start gap-6 md:flex-row md:items-end md:justify-between lg:gap-8',
        )}
      >
        <div className="flex w-full flex-col items-start justify-start gap-6 sm:max-w-[569px]">
          {!!category?.breadcrumb && <Breadcrumbs breadcrumb={category.breadcrumb} />}
          <h1 className={cn('font-medium tracking-tighter text-black', isDesktop ? 'h2' : 'h1')}>
            {category?.name}
          </h1>
          <button
            type="button"
            className="flex cursor-pointer items-center justify-start gap-2 text-sm font-medium tracking-tighter text-primary"
            onClick={() => {
              const el = document.getElementById('category-content');
              if (!el) return;
              const headerHeight = parseFloat(
                getComputedStyle(document.documentElement).getPropertyValue('--header-height') ||
                  '0',
              );
              const top = el.getBoundingClientRect().top + window.scrollY - headerHeight;
              window.scrollTo({
                top,
                behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
                  ? 'instant'
                  : 'smooth',
              });
            }}
          >
            {t('text3839')} <LuArrowDown size={20} />
          </button>
        </div>
        {hasSubCategories && (
          <div className="flex w-full flex-col items-start justify-start gap-3 md:max-w-[783px]">
            <Heading tag="h2" title={t('text337')} className={{ title: 'h6 text-[#6B7280]' }} />
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
              {category?.sub_categories?.map(subCategory => (
                <SubCategoryCard key={subCategory.id} subCategory={subCategory} />
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}

function SubCategoryCard({ subCategory }: { subCategory: SubCategoriesEntity }) {
  return (
    <Link
      href={subCategory?.slug ?? ''}
      prefetchOnHover
      className="flex w-full items-center justify-between gap-3 rounded-[20px] border p-3 transition-all hover:bg-border/25"
    >
      <Heading title={subCategory?.name} tag="h3" className={{ title: 'h5' }} />
      <MdOutlineKeyboardArrowRight size={24} className="text-[#D1D5DB]" />
    </Link>
  );
}
